import React, { useState, useEffect, useMemo } from 'react';
import { Spin, Radio } from 'antd';
import rawQuizData from './short_questions.json';
import CareerResults from './CareerResults';
import FloatingProgress from './FloatingProgress';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';

const CareerQuizEngine = () => {
    const [currentPart, setCurrentPart] = useState('parta'); // 'parta' | 'partb' | 'results'
    const [currentIndex, setCurrentIndex] = useState(0);
    const [scores, setScores] = useState({}); // { R: 0, I: 0, ... }
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [history, setHistory] = useState([]);
    const [showPartBOptions, setShowPartBOptions] = useState(false);

    // Randomize Options for Part B
    const quizData = useMemo(() => {
        // Deep copy to check
        const data = JSON.parse(JSON.stringify(rawQuizData));
        if (data.partb) {
            data.partb.forEach(question => {
                if (question.options && Array.isArray(question.options)) {
                    // Fisher-Yates Shuffle
                    for (let i = question.options.length - 1; i > 0; i--) {
                        const j = Math.floor(Math.random() * (i + 1));
                        [question.options[i], question.options[j]] = [question.options[j], question.options[i]];
                    }
                }
            });
        }
        return data;
    }, []);

    // Initialize scores
    useEffect(() => {
        const initialScores = {};
        setScores(initialScores);
    }, []);

    const currentQuestions = quizData[currentPart] || [];
    const currentQuestion = currentQuestions[currentIndex];
    const isLastQuestion = currentIndex === currentQuestions.length - 1;

    // Progress Calculation
    const totalQuestionsPartA = quizData.parta.length;
    const totalQuestionsPartB = quizData.partb.length;
    const itemsAnswered = currentPart === 'parta'
        ? currentIndex
        : totalQuestionsPartA + currentIndex;
    const totalItems = totalQuestionsPartA + totalQuestionsPartB;


    const handleAnswerSelect = (val) => {
        setSelectedAnswer(val);
    };

    const handleNext = () => {
        // Part B Logic: 2-step flow
        if (currentPart === 'partb' && !showPartBOptions) {
            setShowPartBOptions(true);
            return;
        }

        if (selectedAnswer === null) return;

        // Capture Answer for History
        let answerText = "";
        let scoreToAdd = 0;
        let typeToUpdate = currentQuestion.type;

        if (currentPart === 'parta') {
            answerText = `Scale: ${selectedAnswer}`;
            // Part A: 1-5 Scale. Answer >= 3 -> Score = 1
            if (parseInt(selectedAnswer) >= 3) {
                scoreToAdd = 1;
            }
        } else if (currentPart === 'partb') {
            const selectedOptionInfo = currentQuestion.options[selectedAnswer];
            answerText = selectedOptionInfo?.text || "";
            scoreToAdd = selectedOptionInfo?.weight || 0;
        }

        // Update Scores
        setScores(prev => ({
            ...prev,
            [currentPart]: {
                ...(prev[currentPart] || {}),
                [typeToUpdate]: ((prev[currentPart] && prev[currentPart][typeToUpdate]) || 0) + scoreToAdd
            }
        }));

        // Update History
        setHistory(prev => [...prev, {
            question: currentQuestion.title,
            answer: answerText
        }]);

        // Move to next
        setSelectedAnswer(null);
        setShowPartBOptions(false); // Reset for next question

        if (isLastQuestion) {
            if (currentPart === 'parta') {
                setCurrentPart('partb');
                setCurrentIndex(0);
            } else {
                setCurrentPart('results');
            }
        } else {
            setCurrentIndex(prev => prev + 1);
        }
    };

    if (currentPart === 'results') {
        return <CareerResults scores={scores} />;
    }

    if (!currentQuestion) return <div className="p-10 text-center"><Spin /></div>;

    // Determine what to show based on Part A vs Part B (+ step)
    const showImage = currentPart === 'parta' || (currentPart === 'partb' && !showPartBOptions);
    const showOptions = currentPart === 'parta' || (currentPart === 'partb' && showPartBOptions);
    const isNextDisabled = showOptions && selectedAnswer === null;

    return (
        <div className="flex flex-col items-center justify-center min-h-[70vh] px-4 w-full animate-fadeIn pb-20">
            {/* Floating Progress Indicator */}
            <FloatingProgress
                current={itemsAnswered}
                total={totalItems}
                history={history}
            />

            <div key={`${currentPart}-${currentIndex}-${showPartBOptions}`} className="w-full max-w-3xl bg-white/50 backdrop-blur-md p-0 md:p-10 rounded-3xl shadow-sm border border-white/40 mt-8 animate-fadeIn">
                {/* Question Image - Hidden in Part B Step 2 */}
                {currentQuestion.imgurl && currentQuestion.imgurl.trim() !== "" && showImage && (
                    <div className="flex justify-center mb-8 w-full">
                        <img
                            src={currentQuestion.imgurl}
                            alt="Question Context"
                            className="rounded-xl shadow-lg w-full max-h-[300px] object-cover object-top"
                        />
                    </div>
                )}

                {/* Question Title */}
                <h2 className={`large-gradient-heading mb-12 leading-tight text-left ${currentPart === 'partb' && showPartBOptions ? 'text-[16px]' : 'text-2xl md:text-3xl'}`}>
                    {currentQuestion.title}
                </h2>

                {/* Question Text (Part B mainly) */}
                {currentQuestion.text && (
                    <p className="brand-text text-lg text-[var(--color-subtext)] mb-8 text-left leading-relaxed">
                        {currentQuestion.text}
                    </p>
                )}

                {/* Options - Hidden in Part B Step 1 */}
                {showOptions && (
                    <div className="w-full flex justify-center mt-[20px]">
                        {currentPart === 'parta' ? (
                            // 1-5 Scale for Part A
                            <div className="flex flex-col md:flex-row gap-4 w-full justify-center items-center">
                                <div className="flex justify-between w-full max-w-lg bg-gray-50 p-3 rounded-[2rem] shadow-inner">
                                    {[1, 2, 3, 4, 5].map((val) => (
                                        <button
                                            key={val}
                                            onClick={() => handleAnswerSelect(val)}
                                            className={`
                                            w-12 h-12 md:w-16 md:h-16 rounded-xl flex items-center justify-center text-xl font-bold transition-all duration-300
                                            ${selectedAnswer === val
                                                    ? 'bg-gradient-to-br from-[var(--color-brand)] to-[var(--color-highlight)] text-white scale-105 shadow-md'
                                                    : 'bg-white text-gray-500 shadow hover:bg-gray-100 hover:shadow-md'}
                                        `}
                                        >
                                            {val}
                                        </button>
                                    ))}
                                </div>
                                <div className="flex justify-between w-full max-w-lg text-[var(--color-subtext)] px-4 mt-4 md:mt-0 font-bold brand-font text-lg md:text-xl">
                                    <span>Disagree</span>
                                    <span className="gradient-small text-lg md:text-xl">Agree</span>
                                </div>
                            </div>
                        ) : (
                            // Options for Part B
                            <div className="flex flex-col gap-4 w-full">
                                {currentQuestion.options.map((opt, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => handleAnswerSelect(idx)}
                                        className={`
                                        text-left p-5 rounded-xl border-2 transition-all duration-300 brand-text text-lg relative overflow-hidden group
                                        ${selectedAnswer === idx
                                                ? 'border-[var(--color-brand)] bg-[var(--color-brand)]/5 text-black'
                                                : 'border-transparent bg-white/60 hover:bg-white/90 hover:shadow-md'}
                                    `}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className={`
                                            w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0
                                            ${selectedAnswer === idx ? 'border-[var(--color-brand)]' : 'border-gray-300'}
                                        `}>
                                                {selectedAnswer === idx && <div className="w-3 h-3 rounded-full bg-[var(--color-brand)]"></div>}
                                            </div>
                                            <span>{opt.text}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Navigation */}
                <div className="mt-12 flex justify-center">
                    <button
                        onClick={handleNext}
                        disabled={isNextDisabled}
                        className={`
                            rotating-border-button group transition-all duration-500
                            ${isNextDisabled ? 'opacity-50 cursor-not-allowed grayscale' : 'hover:scale-105 active:scale-95'}
                        `}
                    >
                        <span className="gradient-small px-10 py-1 text-xl flex items-center gap-3">
                            {isLastQuestion && currentPart === 'partb' && showPartBOptions ? 'Finish' : 'Next'} <ArrowRightOutlined className="text-sm" />
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CareerQuizEngine;
